import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { FocuslinkService } from 'src/app/core/services/focuslink.service';
import { KioskPaymentService } from 'src/app/core/services/kiosk-payment.service';
import { DCDirectRStream } from 'src/app/shared/models/DTO/FocusPayDatacap/DatacapAuthResponse.dto';
import { KioskPaymentRequest } from 'src/app/shared/models/DTO/FocusPayDatacap/KioskPaymentRequest.dto';
import { I4goPaymentResponseDTO } from 'src/app/shared/models/DTO/I4goPaymentRequest.dto';
import { LogLevel } from 'src/app/shared/models/enums/log-level.enum';

@Component({
	selector: 'app-kiosk-payment',
	templateUrl: './kiosk-payment.component.html',
	styleUrls: ['./../online-order.component.scss'],
})
export class KioskPaymentComponent implements OnInit, OnDestroy {
	@Input() checkKey: string;
	@Input() orderTotal: number;
	@Input() orderTip: number;
	@Input() orderTax: number;
	@Input() venueKey: number;

	@Output() paymentResponse: EventEmitter<I4goPaymentResponseDTO> = new EventEmitter<I4goPaymentResponseDTO>();

	paymenResponseSubscription: Subscription;
	paymentResponseReceived: boolean = false;
	paymentRequest: KioskPaymentRequest = undefined;
	defaultUserMessage: string = 'For credit card payments, please use the payment pad to complete your transaction.';
	activeUserMessage: string = this.defaultUserMessage;
	displayRetry: boolean = false;
	errorMessage: string = undefined;

	dcDirectTimeout: number;
	paymentRequestPollingInterval: number;

	constructor(public kioskPaymentService: KioskPaymentService, private focuslink: FocuslinkService) {}

	ngOnDestroy(): void {
		this.kioskPaymentService.deviceService.disconnect(this.venueKey);
		this.paymenResponseSubscription.unsubscribe();
		clearTimeout(this.dcDirectTimeout);
		clearInterval(this.paymentRequestPollingInterval);
	}

	ngOnInit(): void {
		this.kioskPaymentService.deviceService.connect(this.venueKey).then((data) => {
			this.initPaymentRequest(this.checkKey, this.orderTotal, this.orderTip);
		});
	}

	async initPaymentRequest(checkKey: string, orderTotal: number, orderTip: number) {
		const kioskPaymentRequest = await this.kioskPaymentService.buildPaymentRequest(checkKey, orderTotal, orderTip);

		await this.kioskPaymentService.subscribeToPaymentResponse();
		this.paymentRequest = await this.kioskPaymentService.performPaymentRequest(this.venueKey, kioskPaymentRequest);
		this.paymenResponseSubscription = this.kioskPaymentService.paymentResponse.subscribe((data) => {
			this.paymentResponseReceived = true;
			clearTimeout(this.dcDirectTimeout);
			this.handleKioskResponse(data.RStream);
		});

		this.dcDirectTimeout = window.setTimeout(() => this.timeoutFunction(), 90000);
		this.paymentRequestPollingInterval = window.setInterval(async () => await this.intervalPollPaymentRequestStatus(), 30000);
	}

	handleKioskResponse(rStream: DCDirectRStream) {
		switch (rStream.CmdStatus) {
			case 'Approved':
				this.paymentResponse.emit(this.dcDirectToI4go(rStream));
				break;
			case 'Declined':
				this.activeUserMessage = "Sorry, your payment couldn't be processed. Please try again.";
				this.displayRetry = true;
				break;
			case 'Error':
				//error condition has occurred
				this.activeUserMessage = 'Sorry, there was an error processing your transaction.';
				this.displayRetry = true;
				this.errorMessage = rStream.TextResponse;
				clearTimeout(this.dcDirectTimeout);
		}

		this.focuslink.log(this.venueKey, 'kiosk-payment-success', LogLevel.INFO, {
			checkKey: this.checkKey,
			response: rStream,
		});

		this.kioskPaymentService.deviceService.disconnect(this.venueKey);
	}

	dcDirectToI4go(dcDirectResponse: DCDirectRStream): I4goPaymentResponseDTO {
		dcDirectResponse.licenseKey = this.kioskPaymentService.deviceService.licenseKey;
		dcDirectResponse.dcDirect = true;

		return new I4goPaymentResponseDTO({
			amount: +dcDirectResponse.Authorize,
			displayName: dcDirectResponse.AcctNo,
			token: dcDirectResponse.PayAPI_Id,
			expirationMonth: dcDirectResponse.ExpDateMonth,
			expirationYear: dcDirectResponse.ExpDateYear,
			firstName: dcDirectResponse.CardholderName?.includes('/') ? dcDirectResponse.CardholderName?.split('/')[1] : undefined,
			lastName: dcDirectResponse.CardholderName?.includes('/') ? dcDirectResponse.CardholderName?.split('/')[0] : undefined,
			pspReference: dcDirectResponse.RecordNo,
			extendedCardData: JSON.stringify(dcDirectResponse),
			sendToProcessor: false,
			kioskPaymentRequestKey: this.paymentRequest.sk,
		});
	}

	async intervalPollPaymentRequestStatus() {
		try {
			const resp = await this.kioskPaymentService.paymentRequestCheckStatus(this.venueKey, this.paymentRequest.sk);
			if (resp && resp.response) {
				clearInterval(this.paymentRequestPollingInterval);
				clearTimeout(this.dcDirectTimeout);
				this.handleKioskResponse(resp.response.RStream);
				this.paymentResponseReceived = true;
			}
		} catch (error) {}
	}

	async retry() {
		this.focuslink.log(this.venueKey, 'kiosk-payment-retry', LogLevel.ERROR, {
			checkKey: this.checkKey,
		});
		this.displayRetry = false;

		this.paymenResponseSubscription.unsubscribe();

		this.initPaymentRequest(this.checkKey, this.orderTotal, this.orderTip);
	}

	timeoutFunction() {
		if (!this.paymentResponseReceived) {
			this.focuslink.log(this.venueKey, 'kiosk-payment-timeout', LogLevel.ERROR, {
				checkKey: this.checkKey,
			});
			this.displayRetry = true;
			this.activeUserMessage = 'Problem communicating to payment pad. Please try again.';
		}
	}
}
